import { useSafeQuery } from '@gen2/hooks';
import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import {
  connectInt,
  createIntFolder,
  disconnectInt,
  getStorageFolders,
  GetIntFoldersParams,
  GetIntFoldersResponse,
  getStorageSettings,
  saveIntFolder,
  TIntegrationType,
  getSharepointSites,
  SharepointSitesResponse,
  GetIntSettingsResponse,
  getSharepointDocumentLibraries,
  SharepointDocumentLibrariesResponse,
  syncFolders,
  getWorkatoEmbeddedLink,
  getWorkatoManagedConnection,
  getAllWorkatoManagedConnections,
} from './api';

export enum IntegrationKeys {
  connectInt = 'connectInt',
  disconnectInt = 'disconnectInt',
  getStorageFolders = 'getStorageFolders',
  saveIntFolder = 'saveIntFolder',
  storageSettings = 'storageSettings',
  createStorageFolder = 'createStorageFolder',
  getSharepointSites = 'getSharepointSites',
  getSharepointDocumentLibraries = 'getSharepointDocumentLibraries',
  syncFolders = 'syncFolders',
  getWorkatoEmbeddedLink = 'getWorkatoEmbeddedLink',
  getWorkatoManagedConnection = 'getWorkatoManagedConnection',
  getAllWorkatoManagedConnections = 'getAllWorkatoManagedConnections',
}

export const useStorageFoldersQuery = (
  vendor: TIntegrationType,
  params?: GetIntFoldersParams,
  options?: UseQueryOptions<GetIntFoldersResponse['data']>,
) => {
  return useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [
      IntegrationKeys.getStorageFolders,
      vendor,
      params?.folder_id,
      params?.site_id,
      params?.search,
    ],
    queryFn: async (): Promise<GetIntFoldersResponse['data']> => {
      const response = await getStorageFolders(vendor, params);
      return response.data?.data;
    },
    ...options,
  });
};

export const useConnectIntMutation = () =>
  useMutation({
    mutationKey: [IntegrationKeys.connectInt],
    mutationFn: connectInt,
  });

export const useDisconnectIntMutation = () =>
  useMutation({
    mutationKey: [IntegrationKeys.disconnectInt],
    mutationFn: disconnectInt,
  });

export const useStorageFolderMutation = () =>
  useMutation({
    mutationKey: [IntegrationKeys.saveIntFolder],
    mutationFn: saveIntFolder,
  });

export const useStorageSettings = (
  vendor: TIntegrationType,
  options?: UseQueryOptions<GetIntSettingsResponse>,
) => {
  return useQuery<GetIntSettingsResponse>({
    queryKey: [IntegrationKeys.storageSettings, vendor],
    queryFn: async () => {
      const response = await getStorageSettings(vendor);
      return response.data as GetIntSettingsResponse;
    },
    ...options,
  });
};

export const useCreateStorageFolderMutation = () =>
  useMutation({
    mutationKey: [IntegrationKeys.createStorageFolder],
    mutationFn: createIntFolder,
  });

export const useSharepointSitesQuery = (
  options?: UseQueryOptions<SharepointSitesResponse>,
) => {
  return useSafeQuery<SharepointSitesResponse>(
    [IntegrationKeys.getSharepointSites],
    async () => {
      const res = await getSharepointSites();
      return res.data;
    },
    options,
  );
};

export const useSharepointDocumentLibrariesQuery = (
  options: UseQueryOptions<SharepointDocumentLibrariesResponse> & {
    siteId: string;
  },
) => {
  return useSafeQuery<SharepointDocumentLibrariesResponse>(
    [IntegrationKeys.getSharepointDocumentLibraries, options.siteId],
    async () => {
      const res = await getSharepointDocumentLibraries(options.siteId);
      return res.data;
    },
    {
      ...options,
      enabled: options.enabled && !!options.siteId,
    },
  );
};

export const useSyncFoldersMutation = () =>
  useMutation({
    mutationKey: [IntegrationKeys.syncFolders],
    mutationFn: syncFolders,
  });

export const useWorkatoEmbeddedLinkQuery = (
  type: TIntegrationType,
  options?: UseQueryOptions<{ data: { link: string } }>,
) => {
  return useQuery({
    queryKey: [IntegrationKeys.getWorkatoEmbeddedLink, type],
    queryFn: async () => {
      const res = await getWorkatoEmbeddedLink(type);
      return res.data;
    },
    ...options,
  });
};

export const useWorkatoManagedConnectionQuery = (type: TIntegrationType) => {
  return useQuery({
    queryKey: [IntegrationKeys.getWorkatoManagedConnection, type],
    queryFn: async () => {
      const res = await getWorkatoManagedConnection(type);
      return res.data;
    },
  });
};

export const useWorkatoManagedAllConnectionsQuery = () => {
  return useQuery({
    queryKey: [IntegrationKeys.getAllWorkatoManagedConnections],
    queryFn: async () => {
      const res = await getAllWorkatoManagedConnections();
      return res.data;
    },
  });
};
